import { useQuery } from '@tanstack/react-query'
import type { ChangeEvent } from 'react'
import {
  backgroundFileUrl,
  backgroundsQueryOptions,
  useDeleteBackgroundMutation,
  useUploadBackgroundMutation,
} from '@/entities/background'
import { useSession } from '@/entities/session'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'
import { Spinner } from '@/shared/ui/spinner'
import { toast } from '@/shared/ui/toast'
import { useBackgroundSelection } from '../model/background-store'

const tile =
  'relative flex aspect-video items-center justify-center rounded-control border text-center transition-colors'
const tileActive = 'border-border-accent text-text-primary shadow-glow-accent'
const tileIdle = 'border-border-default text-text-secondary hover:border-border-strong'

export function BackgroundPicker() {
  const { data: backgrounds, isLoading } = useQuery(backgroundsQueryOptions)
  // Загрузка своего фона — только сотруднику: у гостя endpoint отдаёт 401,
  // плитку не показываем вместо тоста об ошибке (гостю доступны системные).
  const { isAuthenticated: canUpload } = useSession()
  const selection = useBackgroundSelection((s) => s.selection)
  const setNone = useBackgroundSelection((s) => s.setNone)
  const setBlur = useBackgroundSelection((s) => s.setBlur)
  const setImage = useBackgroundSelection((s) => s.setImage)
  const upload = useUploadBackgroundMutation()
  const del = useDeleteBackgroundMutation()

  function onUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    upload.mutate(file, {
      onSuccess: (bg) => {
        setImage(bg.id)
        toast.success('Фон загружен')
      },
      onError: () => toast.error('Не удалось загрузить фон'),
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          aria-pressed={selection.kind === 'none'}
          onClick={setNone}
          className={cn(tile, selection.kind === 'none' ? tileActive : tileIdle)}
        >
          <span className="flex flex-col items-center gap-1 text-xs">
            <Icon name="close" size={18} />
            Без фона
          </span>
        </button>

        <button
          type="button"
          aria-pressed={selection.kind === 'blur'}
          onClick={setBlur}
          className={cn(tile, 'overflow-hidden', selection.kind === 'blur' ? tileActive : tileIdle)}
        >
          <span className="absolute inset-0 bg-bg-raised blur-[2px]" aria-hidden />
          <span className="relative text-xs">Размытие</span>
        </button>

        {canUpload && (
          <label
            className={cn(
              tile,
              tileIdle,
              'cursor-pointer',
              upload.isPending && 'pointer-events-none opacity-60',
            )}
          >
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={onUpload}
              disabled={upload.isPending}
            />
            <span className="flex flex-col items-center gap-1 text-xs">
              {upload.isPending ? <Spinner label="Загрузка" /> : <Icon name="plus" size={18} />}
              Загрузить
            </span>
          </label>
        )}
      </div>

      {isLoading ? (
        <Spinner label="Загрузка фонов" />
      ) : (
        // Системные фоны (общие, без удаления) — отдельной секцией над личными.
        (['system', 'personal'] as const).map((group) => {
          const items = (backgrounds ?? []).filter((bg) =>
            group === 'system' ? bg.isSystem : !bg.isSystem,
          )
          if (items.length === 0) return null
          return (
            <div key={group} className="flex flex-col gap-2">
              <span className="label">{group === 'system' ? 'Системные' : 'Мои фоны'}</span>
              <div className="grid grid-cols-3 gap-2">
                {items.map((bg) => {
                  const active = selection.kind === 'image' && selection.imageId === bg.id
                  return (
                    <div key={bg.id} className="relative">
                      <button
                        type="button"
                        aria-pressed={active}
                        aria-label={bg.name}
                        onClick={() => setImage(bg.id)}
                        className={cn(
                          tile,
                          'w-full overflow-hidden',
                          active ? tileActive : tileIdle,
                        )}
                      >
                        <img
                          src={backgroundFileUrl(bg.id)}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </button>
                      {!bg.isSystem && (
                        <button
                          type="button"
                          aria-label={`Удалить фон ${bg.name}`}
                          onClick={() => del.mutate(bg.id)}
                          className="absolute right-1 top-1 z-10 flex h-6 w-6 items-center justify-center rounded-pill bg-bg-base/80 text-text-secondary transition-colors hover:text-accent"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}
